import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePageSEO } from '@/hooks/usePageSEO'

interface PricingPack {
  name: string
  price: string
  duration: string
  description: string
  badge: string
  features: string[]
}

interface CampusPack {
  name: string
  price: string
  description: string
  features: string[]
}

export default function Pricing() {
  const { t } = useTranslation('pricing')
  const packs = t('packs', { returnObjects: true }) as PricingPack[]
  const campus = t('campus', { returnObjects: true }) as CampusPack
  const mainColor = '#525FE1'

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12">
        <section className="mx-auto max-w-3xl text-center">
          <span
            className="inline-flex rounded-full px-4 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: mainColor }}
          >
            {t('hero.badge')}
          </span>
          <h1 className="mt-6 text-4xl font-bold text-gray-900 md:text-5xl">
            {t('hero.title')}
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            {t('hero.description')}
          </p>
          <p className="mt-3 text-sm font-medium text-gray-500">
            {t('labels.taxNote')}
          </p>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-3">
          {packs.map((pack, index) => (
            <article
              key={pack.name}
              className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{pack.name}</h2>
                {index === 1 ? (
                  <span
                    className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                    style={{ backgroundColor: mainColor }}
                  >
                    {pack.badge}
                  </span>
                ) : null}
              </div>

              <p className="mt-3 text-sm leading-6 text-gray-600">
                {pack.description}
              </p>

              <div className="mt-6 border-t border-gray-100 pt-6">
                <p className="text-3xl font-bold text-gray-900">{pack.price}</p>
                <p className="mt-2 text-sm text-gray-500">
                  {t('labels.duration')}: {pack.duration}
                </p>
              </div>

              <ul className="mt-6 flex-1 space-y-3 text-sm text-gray-700">
                {pack.features.map((feature) => (
                  <li key={feature} className="flex gap-3">
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 shrink-0"
                      style={{ color: mainColor }}
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/demo"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: mainColor }}
              >
                {t('hero.primaryCta')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </section>

        <section className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-lg md:p-8">
          <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
            <div>
              <span
                className="inline-flex rounded-full px-3 py-1 text-xs font-semibold text-white"
                style={{ backgroundColor: mainColor }}
              >
                {t('labels.customOffer')}
              </span>
              <h2 className="mt-4 text-3xl font-bold text-gray-900">
                {campus.name}
              </h2>
              <p className="mt-3 text-gray-600">{campus.description}</p>
              <p className="mt-6 text-3xl font-bold text-gray-900">
                {campus.price}
              </p>
            </div>

            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t('labels.features')}
              </p>
              <ul className="mt-4 grid gap-3 text-sm text-gray-700 sm:grid-cols-2">
                {campus.features.map((feature) => (
                  <li key={feature} className="flex gap-3 rounded-xl bg-gray-50 p-4">
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 shrink-0"
                      style={{ color: mainColor }}
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                  style={{ backgroundColor: mainColor }}
                >
                  {t('hero.primaryCta')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contactUs"
                  className="inline-flex items-center justify-center rounded-lg border px-5 py-3 text-sm font-semibold transition hover:bg-blue-50"
                  style={{ borderColor: mainColor, color: mainColor }}
                >
                  {t('hero.secondaryCta')}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}

export const Route = createFileRoute('/pricing')({
  component: PricingRoute,
})

function PricingRoute() {
  const { i18n } = useTranslation('pricing')
  const isFrench = i18n.language?.startsWith('fr')

  usePageSEO({
    title: isFrench
      ? 'Tarifs JeridSchool - Packs planning et administration scolaire'
      : 'JeridSchool Pricing - Planning and school administration packs',
    description: isFrench
      ? 'Decouvrez les packs JeridSchool HT pour planning automatique, examens, administration scolaire et offre campus par etudiant.'
      : 'Discover JeridSchool excluding-tax packs for automatic planning, exams, school administration, and campus pricing per student.',
    keywords: isFrench
      ? 'tarifs JeridSchool HT, pack planning scolaire, emploi du temps automatique, administration scolaire Tunisie'
      : 'JeridSchool pricing, school planning pack, automatic timetable, school administration Tunisia',
    path: '/pricing',
  })

  return <Pricing />
}
